import {
  endsWith, floor, replace, toLower,
  trim,
} from 'lodash';
import type { PublicShow } from '@sda/interfaces/show';
import type { User } from '@sda/interfaces/user';
import type { HyperwalletAccount } from '@sda/interfaces/user/hyperwallet';

/**
 * Generates a full name for the specified user
 * If the user doesn't have a name set, use their email address or other ID
 * @param  {User} user
 * @return {string}
 */
export const getDisplayName = (user: Partial<Pick<User, 'firstName' | 'lastName' | 'email'>>): string => {
  if (!user) {
    return '';
  }

  if (!user.firstName && !user.lastName) {
    return user.email || 'N/A';
  }

  return `${user.firstName} ${user.lastName}`;
};

/**
 * Splits a name string and returns all of its parts
 * @param  {string} val
 * @return {any}
 */
export const getNameParts = (val: string): { firstName: string, lastName?: string } => {
  const name = trim(val);
  const [first, ...lastParts] = name.split(' ');

  return {
    firstName: first,
    lastName: lastParts.length ? lastParts.join(' ') : '',
  };
};

/*
 * Formats the `from` string to output `name` via Side Door.
 * If no name is passed, just use the SUPPORT_EMAIL_FULL.
 * @param  {string} [name] An optional name to include in the from field
 * @return {string}
 */
export const formatFromSupportEmailField = (name?: string): string => {
  const base = process.env.SUPPORT_EMAIL_FULL;

  return name ? `${name.replace(/[^\w\s]/g, '')} via ${base}` : base;
};

/**
 * Turns the given name into its possessive form (English only)
 * @param  {string} name
 * @return {string}
 */
export const possessive = (name: string): string => {
  if (endsWith(name, 's')) {
    return `${name}'`;
  }

  return `${name}'s`;
};

/**
 * Takes the given string and normalizes it
 * @param  {string} str
 * @return {string}
 */
export const normalize = (str: string): string => trim(toLower(str)).normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * Takes the given file name and attempts to normalize it
 * @param  {string} fileName
 * @return {string}
 */
export const normalizeFileName = (fileName: string): string => {
  const formattedSrc = fileName.substring(fileName.indexOf('-') + 1);
  return normalize(formattedSrc);
};

/**
 * Remove any additions to the show title that may have happened
 * @param  {string} title
 * @return {string}
 */
export const normalizeShowTitle = (title: string): string => replace(replace(replace(replace(title, '(SOLD OUT) ', ''), 'CANCELLED - ', ''), '(CANCELLED) ', ''), '(Cancelled) ', '');

/**
 * Creates a plural noun by adding "s" or "es" to the end of a word
 * @param  {string} noun
 * @param  {number} count
 * @return {string}
 */
export const pluralizeNoun = (noun: string, count = 1): string => {
  if (count === 0 || count > 1) {
    if (noun[noun.length - 1] === 's') {
      return `${noun}es`;
    }

    return `${noun}s`;
  }

  return noun;
};

/**
 * Creates a plural noun by adding "s" or "es" to the end of a word
 * AND includes the count
 * @param  {string} noun
 * @param  {number} count
 * @return {string}
 */
export const pluralize = (noun: string, count = 1): string => {
  const pluralizedNoun = pluralizeNoun(noun, count);

  return `${count} ${pluralizedNoun}`;
};

/**
 * Given a string, return an escaped version of the string
 * from : https://stackoverflow.com/a/1144788
 * @param  {string} str string for regex escaping
 * @return {string}
 */
const escapeRegExp = (str: string): string => str && str.replace(/([.*+?^=!:${}()|[\]/\\])/g, '\\$1');

/**
 * Given a string, a target substring, and a replacement value, return a replaced string
 * with all targets within the string replaced
 * from : https://stackoverflow.com/a/1144788
 * @param  {string} str base string
 * @param  {string} target target value to be replaced
 * @param  {string} replacement replacement value
 * @return {string}
 */
export const replaceAll = (str: string, target: string, replacement: string): string => str && str.replace(new RegExp(escapeRegExp(target), 'g'), replacement);

/**
 * Given a string, return a string that has word replacements for 3 characters ('&', '/', '.')
 * @param  {string} string that is to be sanitized of specific characters
 * @return {string}
 */
export const getSanitizedString = (string: string): string => {
  let str = string;

  str = replaceAll(str, '&', ' And ');
  str = replaceAll(str, '/', ' Slash ');
  str = replaceAll(str, '.', ' Dot ');

  return str;
};

/**
 * strips out line breaks from a string
 * @param  {string} String to remove line breaks from
 */
export const removeLineBreaks = (string: string): string => {
  if (typeof string !== 'string') {
    return string;
  }

  return string.replace(/(\r\n|\n|\r)/gm, ' ');
};

/**
 * Returns name to display for a hyperwallet billing account
 * @param {object} HyperwalletAccount
 * @returns {string}
 */
export const getBillingAccountName = (account: Partial<HyperwalletAccount>) => account.displayName || `${account.firstName} ${account.lastName}`;

/**
 * Gets the proper MIME type from the file extension.
 * @param {string} uri
 */
export const getImageMimeFromExtension = (uri: string): string => {
  const ext = uri
    .split('.') // extension
    .pop()
    .split('?') // without query string
    .shift()
    .toLowerCase(); // normalized

  const isJpeg = ext === 'jpg' || ext === 'jpeg';

  return isJpeg ? 'image/jpeg' : 'image/png';
};

/**
 * Gets the city and region of a show, show call, or venue
 * @param show
 */
export const getEventLocation = (show: Pick<PublicShow, 'city' | 'region' | 'country'>): string => {
  if (!show) {
    return '';
  }

  let result = '';

  if (show.city) {
    result = show.city;
  }

  // Fix for some addresses having a region set to "null" as a string in Firestore
  // Issue resolved in v4.0.5
  const hasRegion = show.region && show.region !== 'null';

  if (show?.city && (hasRegion || show.country)) {
    result += ', ';
  }

  if (hasRegion) {
    result += show.region;
  } else if (show.country) {
    result += show.country;
  }

  return result;
};

/**
 * Utility for generating a string used for displaying a profile rating
 * @param  {number} score
 * @param  {number} numReviews
 * @return {string}
 */
export const getRatingString = (score: number, numReviews: number) => {
  const scoreFloat = parseFloat(score as any);
  const numReviewsFloat = parseFloat(numReviews as any);

  if (numReviewsFloat > 0 && scoreFloat > 0) {
    return `${score} (${numReviews})`;
  }

  return 'New!';
};

/**
 * Generates a rounded number of followers
 * @param  {number} followers
 * @return {number}
 */
export const getRoundedFollowers = (followers: number, notation: Intl.NumberFormatOptions['notation'] = 'standard') => {
  if (!followers) {
    return null;
  }

  if (followers < 100) {
    return '<100';
  }

  const followerString = `${followers}`;

  return `${floor(followers, (followerString.length - 1) * -1).toLocaleString('en', { notation, maximumSignificantDigits: 2 })}+`; // We want to round the highest digit only
};
